import React, {
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'class-names';
import Button from 'react-bootstrap/Button';
import Loader from 'react-loader-spinner';
import { ChevronLeft } from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';

import * as AUTH_SELECTORS from '../../../store/selectors/auth';

import * as AUTH_ACTIONS from '../../../store/actions/auth';

import TextInput from '../../ui/TextInput/TextInput';

import { SIGN_IN_STEP } from '../../../const/auth/SIGN_IN_STEP';

import styles from './SignInForm.module.scss';

const Footer = () => {
    const { t } = useTranslation('common');

    const dispatch = useDispatch();

    const {
        step,
        isLoading,
    } = useSelector(AUTH_SELECTORS.authSelector);

    const handleResetPassword = useCallback(() => {
        dispatch(AUTH_ACTIONS.requestResetPasswordCode());
    }, [dispatch]);

    return (
        <div className="d-flex flex-column justify-content-center align-items-center w-100">
            <Button
                form="sign-in-form"
                type="submit"
                size="lg"
                variant="tint"
                className={styles.button}
                disabled={isLoading}
                data-testid="sign-in-button"
            >
                {isLoading ? (
                    <Loader
                        className="d-block text-center"
                        type="TailSpin"
                        color="#fff"
                        height={28}
                        width={28}
                    />
                ) : (
                    t(`signInScreen.${step || 'continue'}`)
                )}
            </Button>

            {[SIGN_IN_STEP.LOGIN, SIGN_IN_STEP.VERIFY_PASSWORD].includes(step) && (
                <button
                    onClick={handleResetPassword}
                    className={styles.link}
                    disabled={isLoading}
                >
                    <p className="text-center mt-3 mb-0">
                        <Trans
                            i18nKey="common:signInScreen.forgotYourPassword"
                            components={[<span/>]}
                        >
                            Forgot your password? <span>Reset</span>
                        </Trans>
                    </p>
                </button>
            )}
        </div>
    );
};

const SignInForm = (props) => {
    const { showFooter } = props;

    const { t } = useTranslation('common');

    const dispatch = useDispatch();

    const {
        step,
        isLoading,
        checkedEmail,
        validationErrors,
    } = useSelector(AUTH_SELECTORS.authSelector);

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    const emailRef = useRef(null);
    const passwordRef = useRef(null);

    const handleSubmit = useCallback((e) => {
        e.preventDefault();
        switch (step) {
            case SIGN_IN_STEP.LOGIN:
            case SIGN_IN_STEP.VERIFY_PASSWORD: {
                return dispatch(AUTH_ACTIONS.verifyPassword(password));
            }
            default: {
                return dispatch(AUTH_ACTIONS.checkEmail(email));
            }
        }
    }, [
        step,
        dispatch,
        password,
        email,
    ]);

    const handleEmailChange = useCallback((value) => {
        setEmail(value);
    }, []);

    const handlePasswordChange = useCallback((value) => {
        setPassword(value);
    }, []);

    const handleScrollToRef = useCallback(({ ref }) => {
        window.scrollTo(0, ref.current.offsetTop);
    }, []);

    const handleScrollToEmail = useCallback(() => {
        handleScrollToRef({ ref: emailRef });
    }, [handleScrollToRef]);

    const handleScrollToPassword = useCallback(() => {
        handleScrollToRef({ ref: passwordRef });
    }, [handleScrollToRef]);

    const handleGoBack = useCallback(() => {
        dispatch(AUTH_ACTIONS.resetStep());
    }, [dispatch]);

    useEffect(() => {
        if ([SIGN_IN_STEP.LOGIN, SIGN_IN_STEP.VERIFY_PASSWORD].includes(step)) {
            passwordRef.current?.focus?.();
        } else {
            emailRef.current?.focus?.();
        }
    }, [step]);

    return (
        <form
            id="sign-in-form"
            onSubmit={handleSubmit}
            className={styles.container}
        >
            {step && (
                <div className="w-100 mb-4">
                    <button
                        type="button"
                        name="back"
                        className={classNames(
                            styles.backButton,
                            'bg-transparent border-0 px-0 ml-n2',
                        )}
                        onClick={handleGoBack}
                    >
                        <ChevronLeft size={24} />
                        <span
                            className="small text-uppercase font-weight-bold"
                            style={{ position: 'relative', top: 1 }}
                        >
                            {t('signInScreen.back')}
                        </span>
                    </button>
                </div>
            )}

            <TextInput
                ref={emailRef}
                type="email"
                name={t('signInScreen.emailAddress')}
                value={step ? checkedEmail : email}
                setInputValue={handleEmailChange}
                validationError={validationErrors?.email}
                onFocus={handleScrollToEmail}
                disabled={step || isLoading}
                data-testid="data-test-email"
            />

            {[SIGN_IN_STEP.LOGIN, SIGN_IN_STEP.VERIFY_PASSWORD].includes(step) && (
                <TextInput
                    ref={passwordRef}
                    type="password"
                    name={t('signInScreen.password')}
                    value={password}
                    rules={{ minLength: 2 }}
                    setInputValue={handlePasswordChange}
                    validationError={validationErrors?.password}
                    onFocus={handleScrollToPassword}
                />
            )}

            {showFooter && (
                <div className="mt-3 w-100">
                    <Footer />
                </div>
            )}
        </form>
    );
};

SignInForm.Footer = Footer;

SignInForm.propTypes = {
    showFooter: PropTypes.bool,
};

SignInForm.defaultProps = {
    showFooter: true,
};

export default SignInForm;
